import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import api from './api';
import {
  readStorageJSON,
  removeStorageValue,
  writeStorageJSON,
} from './utils/storage';
import {
  clearPersistedAccessToken,
  isModernAccessToken,
  readLegacyAccessToken,
  requestRefreshWithRetry,
} from './security/authSession';

const isGoogleTranslateProxyHost = (hostname = '') =>
  typeof hostname === 'string' &&
  (hostname.endsWith('.translate.goog') ||
    hostname === 'translate.googleusercontent.com');

const buildGoogleTranslateLoginPath = (search = '') => {
  if (!search) return '/';
  const params = new URLSearchParams(search);
  const translatorParams = new URLSearchParams();
  params.forEach((value, key) => {
    if (key.startsWith('_x_tr_')) translatorParams.set(key, value);
  });
  const query = translatorParams.toString();
  return query ? `/?${query}` : '/';
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const legacyTokenRef = useRef(null);
  if (legacyTokenRef.current === null) {
    legacyTokenRef.current = readLegacyAccessToken();
  }

  const [user, setUser] = useState(() => readStorageJSON('user') ?? null);
  const [token, setToken] = useState(() => legacyTokenRef.current || null);
  const [isInitializing, setIsInitializing] = useState(true);
  const refreshPromiseRef = useRef(null);
  const bootstrapStartedRef = useRef(false);

  const redirectToLogin = useCallback(() => {
    if (typeof window === 'undefined') return;
    const loginPaths = ['/', '/login'];
    if (loginPaths.includes(window.location.pathname)) return;

    const { hostname, search } = window.location;
    if (isGoogleTranslateProxyHost(hostname)) {
      window.location.replace(buildGoogleTranslateLoginPath(search));
      return;
    }
    window.location.replace('/');
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }, [token]);

  const applySession = useCallback(
    (payload) => {
      const nextToken = payload?.token;
      const nextUser = payload?.user ?? null;
      if (!nextToken) throw new Error('Token inválido');

      setToken(nextToken);
      clearPersistedAccessToken();
      legacyTokenRef.current = isModernAccessToken(nextToken)
        ? ''
        : nextToken;

      if (nextUser) {
        setUser(nextUser);
        writeStorageJSON('user', nextUser);
      } else if (user) {
        writeStorageJSON('user', user);
      }
      return nextToken;
    },
    [user]
  );

  const clearSession = useCallback(() => {
    legacyTokenRef.current = '';
    setUser(null);
    setToken(null);
    removeStorageValue('user');
    clearPersistedAccessToken();
  }, []);

  const refreshSession = useCallback(
    ({ legacyToken = legacyTokenRef.current || '' } = {}) => {
      if (refreshPromiseRef.current) return refreshPromiseRef.current;

      const request = requestRefreshWithRetry(api, { legacyToken })
        .then((res) => applySession(res?.data))
        .finally(() => {
          refreshPromiseRef.current = null;
        });
      refreshPromiseRef.current = request;
      return request;
    },
    [applySession]
  );

  useEffect(() => {
    if (bootstrapStartedRef.current) return;
    bootstrapStartedRef.current = true;
    const legacyToken = legacyTokenRef.current || '';
    clearPersistedAccessToken();

    refreshSession({ legacyToken })
      .catch((error) => {
        const rejectedByServer = Boolean(error?.response);
        if (!legacyToken || rejectedByServer) clearSession();
      })
      .finally(() => {
        setIsInitializing(false);
      });
  }, [clearSession, refreshSession]);

  const login = async (identifier, password) => {
    try {
      const res = await api.post(
        '/auth/login',
        { login: identifier, password },
        { skipAuthHeader: true, withCredentials: true }
      );
      if (res.data?.mfaRequired === true) {
        return res.data;
      }
      applySession(res.data);
      return { authenticated: true, user: res.data?.user ?? null };
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha no login';
      const error = new Error(msg);
      error.code = err.response?.data?.code;
      error.retryAfter = err.response?.headers?.['retry-after'];
      throw error;
    }
  };

  const completeMfa = async ({ challengeToken, code }) => {
    try {
      const res = await api.post(
        '/auth/mfa/verify',
        { challengeToken, code },
        { skipAuthHeader: true, withCredentials: true }
      );
      applySession(res.data);
      return {
        authenticated: true,
        user: res.data?.user ?? null,
        recoveryCodes: Array.isArray(res.data?.recoveryCodes)
          ? res.data.recoveryCodes
          : [],
      };
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha ao validar o código';
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', null, { withCredentials: true });
    } catch (err) {
      console.error('Falha ao registrar logout', err);
    } finally {
      clearSession();
    }
  };

  const updateUser = useCallback((nextUser) => {
    setUser(nextUser);
    writeStorageJSON('user', nextUser);
  }, []);

  useEffect(() => {
    if (!token || isInitializing || typeof window === 'undefined') return undefined;

    let disposed = false;
    let syncInFlight = false;
    let lastSyncAt = 0;
    const minSyncIntervalMs = 15000;

    const syncAuthorizationState = async () => {
      if (
        disposed ||
        syncInFlight ||
        document.visibilityState === 'hidden' ||
        Date.now() - lastSyncAt < minSyncIntervalMs
      ) {
        return;
      }

      lastSyncAt = Date.now();
      syncInFlight = true;
      try {
        const response = await api.get('/auth/me');
        const nextUser = response?.data?.user;
        if (!disposed && nextUser) {
          updateUser(nextUser);
        }
      } catch {
        // O interceptor de autenticação trata sessões inválidas.
      } finally {
        syncInFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncAuthorizationState();
      }
    };

    window.addEventListener('focus', syncAuthorizationState);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.removeEventListener('focus', syncAuthorizationState);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitializing, token, updateUser]);

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        if (status === 401 && token) {
          const originalRequest = error.config ?? null;
          const requestUrl =
            typeof originalRequest?.url === 'string' ? originalRequest.url : '';
          const normalizedUrl = (() => {
            if (!requestUrl) return '';
            if (/^https?:/i.test(requestUrl)) {
              try {
                return new URL(requestUrl).pathname || '';
              } catch {
                return requestUrl;
              }
            }
            return requestUrl;
          })();

          const skipRefresh = Boolean(
            originalRequest?.skipAuthRefresh ||
              originalRequest?.__isRetryRequest ||
              /\/auth\/refresh/i.test(normalizedUrl) ||
              /\/auth\/login/i.test(normalizedUrl) ||
              /\/auth\/mfa\/verify/i.test(normalizedUrl) ||
              /\/auth\/activate/i.test(normalizedUrl)
          );

          if (!skipRefresh) {
            try {
              const nextToken = await refreshSession();
              if (nextToken && originalRequest) {
                if (!originalRequest.headers) originalRequest.headers = {};
                const headers = originalRequest.headers;
                if (typeof headers.set === 'function') {
                  headers.set('Authorization', `Bearer ${nextToken}`);
                } else {
                  headers.Authorization = `Bearer ${nextToken}`;
                }
                originalRequest.__isRetryRequest = true;
                return api(originalRequest);
              }
            } catch {
              // A limpeza abaixo encerra a sessão se o refresh seguro falhar.
            }
          }

          clearSession();
          redirectToLogin();
        }
        return Promise.reject(error);
      }
    );

    return () => api.interceptors.response.eject(interceptor);
  }, [clearSession, redirectToLogin, refreshSession, token]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isInitializing,
        login,
        completeMfa,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
